import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { TrendingUp, ArrowLeft, Clock, Users } from 'lucide-react';

const CommoditiesCaseStudy = () => {
  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <article className="pt-40 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#3B82F6] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-sm text-[#2563EB] font-semibold uppercase tracking-wide">
                  Commodities Trading
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#F8FAFC]">
                  AgriTrade Global
                </h1>
              </div>
            </div>
            <p className="text-xl text-[#94A3B8]">
              Achieving 35% faster response and $4.8M in hedging savings through real time intelligence
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden mb-12 border border-[#2563EB]/20">
            <img
              src="https://images.pexels.com/photos/34381972/pexels-photo-34381972.jpeg"
              alt="Commodities Trading Success"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/60 to-transparent" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Users className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">Global</div>
              <div className="text-sm text-[#94A3B8]">Trading operations</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <TrendingUp className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">35%</div>
              <div className="text-sm text-[#94A3B8]">Faster response</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Clock className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">$4.8M</div>
              <div className="text-sm text-[#94A3B8]">Hedging savings</div>
            </div>
          </div>

          <div className="prose prose-invert prose-lg max-w-none">
            <div className="text-[#94A3B8] leading-relaxed space-y-8">
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-red-500/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-4">The Challenge</h2>
                <p className="text-lg">
                  Volatile markets and outdated manual pricing processes resulted in missed opportunities and suboptimal hedging decisions costing millions annually.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-4">The Solution</h2>
                <p className="text-lg">
                  Real time market intelligence with dynamic hedging strategies. Forward curve predictions and automated risk management integrated with pricing decisions.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-green-500/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-6">The Results</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">35%</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Faster Response</div>
                  </div>
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">$4.8M</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Hedging Savings</div>
                  </div>
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">60%</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Better Forecasts</div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#2563EB]/10 to-transparent border border-[#2563EB]/30 rounded-xl p-8">
                <div className="text-4xl text-[#2563EB] mb-4">"</div>
                <p className="text-[#F8FAFC] text-2xl leading-relaxed mb-6 italic">
                  In commodities, speed is everything. PriceStrike gave us the edge we needed to outmaneuver larger competitors.
                </p>
                <div className="border-t border-[#2563EB]/20 pt-4">
                  <div className="font-semibold text-[#F8FAFC] text-lg">David Kumar</div>
                  <div className="text-[#94A3B8]">Head of Trading</div>
                  <div className="text-sm text-[#2563EB] mt-1">AgriTrade Global</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 p-8 bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-2xl text-center">
            <h3 className="text-3xl font-bold text-[#F8FAFC] mb-4">
              Gain Trading Edge with Intelligence
            </h3>
            <p className="text-[#94A3B8] mb-6 text-lg">
              React faster to market changes with real time pricing intelligence.
            </p>
            <Link to="/pricing" className="inline-block bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-lg font-semibold transition-colors text-lg">
              Explore Pricing Plans
            </Link>
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default CommoditiesCaseStudy;
